import type { AuthUser, OrgMembership } from "@/types";

const PUBLIC_AUTH_PREFIXES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/onboarding",
  "/set-password",
  "/auth/invitations/accept",
];

const TRACKING_ROUTE_PREFIXES = [
  "/dashboard",
  "/live",
  "/employees",
  "/screenshots",
  "/attendance",
  "/reports",
  "/settings",
] as const;

function isPublicAuthPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }

  return PUBLIC_AUTH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function normalizePreferredPath(
  pathname: string | null | undefined,
): string | null {
  if (!pathname || pathname === "/" || isPublicAuthPath(pathname)) {
    return null;
  }

  return pathname;
}

export function getAdminTrackingPath(
  pathname: string | null | undefined,
): string | null {
  if (!pathname) {
    return null;
  }

  if (pathname === "/admin") {
    return "/admin/dashboard";
  }

  if (pathname.startsWith("/admin/")) {
    return pathname;
  }

  const matchedPrefix = TRACKING_ROUTE_PREFIXES.find(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  return matchedPrefix ? `/admin${pathname}` : null;
}

export function getPostAuthPath({
  token,
  user,
  organizations,
  currentOrgId,
  preferredPath,
}: {
  token: string | null;
  user: AuthUser | null;
  organizations: OrgMembership[];
  currentOrgId: string | null;
  preferredPath?: string | null;
}): string | null {
  if (!token || !user) {
    return null;
  }

  if (user.platformRole === "PLATFORM_ADMIN") {
    return "/admin";
  }

  if (user.platformRole === "CRM_MEMBER") {
    if (currentOrgId) {
      return (
        getAdminTrackingPath(normalizePreferredPath(preferredPath)) ||
        "/admin/dashboard"
      );
    }

    if (organizations.length > 0) {
      return "/select-org";
    }

    return "/onboarding";
  }

  if (currentOrgId) {
    return normalizePreferredPath(preferredPath) || "/dashboard";
  }

  if (organizations.length > 0) {
    return "/select-org";
  }

  return "/onboarding";
}
